import { useEffect, useState } from 'react';
import { useStableCallback } from './use-stable-callback';

interface UseWaitForOptions {
   /** Polling interval in milliseconds. Defaults to 50. Pass null to disable polling. */
   interval?: number | null;
   /** Maximum time to poll before giving up, in milliseconds. Defaults to 10000. */
   timeout?: number;
}

/**
 * Hook that polls a predicate function until it returns true or timeout is reached.
 *
 * @param predicate - Function that returns true when the condition is met
 * @param options - Configuration options
 * @returns `false` while polling, `true` when condition met
 *
 * @example
 * ```tsx
 * // Wait for a global API to be available (SSR-safe)
 * const isReady = useWaitFor(() => typeof window !== 'undefined' && !!window.SomeAPI);
 *
 * // Wait with custom timing
 * const isLoaded = useWaitFor(() => document.readyState === 'complete', {
 *   interval: 100,
 *   timeout: 5000,
 * });
 *
 * // Disable polling
 * const value = useWaitFor(() => someCondition, { interval: null });
 * ```
 */
export function useWaitFor(predicate: () => boolean, options: UseWaitForOptions = {}): boolean {
   const { interval = 50, timeout = 10_000 } = options;
   const [ready, setReady] = useState(false);
   const stablePredicate = useStableCallback(predicate);

   useEffect(() => {
      if (interval === null) return;

      if (stablePredicate()) {
         setReady(true);
         return;
      }

      const intervalId = setInterval(() => {
         if (stablePredicate()) {
            setReady(true);
            clearInterval(intervalId);
            clearTimeout(timeoutId);
         }
      }, interval);

      const timeoutId = setTimeout(() => clearInterval(intervalId), timeout);

      return () => {
         clearInterval(intervalId);
         clearTimeout(timeoutId);
      };
   }, [interval, timeout, stablePredicate]);

   return ready;
}
